// ─────────────────────────────────────────────────────────────────────────────
// sessionExpiry — JWT exp validation on bootstrap.
//
// Codex CRITICAL fix: the app previously rehydrated `userSlice` from
// redux-persist (localStorage) and trusted any decodedToken.email indefinitely.
// Expired sessions could still render protected UI and cached contact / user
// data until an API request returned 401.
//
// This utility is called from src/index.js BEFORE the React tree renders. It
// reads the persisted JWT, validates `exp`, and if the token is missing or
// expired it purges the persisted auth + contact slices and clears the
// accessToken from localStorage. The /login redirect happens naturally when
// PermissionGuard sees no decodedToken.email.
//
// Backend ENFORCEMENT NOTE: this is a client-side defense. The backend MUST
// also validate `exp` on every protected request. This utility just prevents
// the *UI* from showing protected content before the first API round-trip.
// ─────────────────────────────────────────────────────────────────────────────

import jwt_decode from "jwt-decode";

const PERSIST_KEY = "persist:root";
const ACCESS_TOKEN_KEY = "accessToken";
const SKEW_SECONDS = 30; // treat as expired if within 30 s of expiry

/**
 * @returns {boolean} true if a valid (non-expired) token exists, false
 * otherwise. Side effect: purges localStorage on invalid/expired token.
 */
export function validateAndPurgeIfExpired() {
  let token = null;
  try {
    token = localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (_) {
    // localStorage may be inaccessible (private mode, etc.) — treat as no session.
    return false;
  }

  if (!token) {
    purgePersisted();
    return false;
  }

  let decoded;
  try {
    decoded = jwt_decode(token);
  } catch (_) {
    // Malformed token — purge and bail.
    purgePersisted();
    return false;
  }

  const expSeconds = typeof decoded?.exp === "number" ? decoded.exp : null;
  if (!expSeconds) {
    // No exp claim — refuse to trust the session indefinitely. Most backends
    // issue tokens with exp; the absence of one is itself a warning sign.
    // eslint-disable-next-line no-console
    console.warn("Session token has no exp claim — purging.");
    purgePersisted();
    return false;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  if (nowSeconds + SKEW_SECONDS >= expSeconds) {
    // Expired (or about to expire within skew window).
    // eslint-disable-next-line no-console
    console.info("Session expired — purging persisted auth + contact state.");
    purgePersisted();
    return false;
  }

  return true;
}

/**
 * Clear accessToken AND wipe the redux-persist root entry so the persisted
 * `userSlice` and `ContactSlice` (the two whitelisted slices in store.js) are
 * not rehydrated stale. After this runs, refresh / next render starts cleanly.
 */
function purgePersisted() {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(PERSIST_KEY);
  } catch (_) {
    // best-effort
  }
}

/**
 * Returns the seconds until expiry (negative if already expired). Useful for
 * scheduling a soft re-validation timer in long-lived tabs.
 */
export function secondsUntilExpiry() {
  let token;
  try {
    token = localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (_) {
    return -1;
  }
  if (!token) return -1;
  try {
    const decoded = jwt_decode(token);
    if (typeof decoded?.exp !== "number") return -1;
    return decoded.exp - Math.floor(Date.now() / 1000);
  } catch (_) {
    return -1;
  }
}

export default validateAndPurgeIfExpired;
